import { useState } from "react";
import { useDrop } from "react-dnd";
import {
  CalendarDndDropResult,
  CalendarDndItem,
  CalendarDndItemTypes,
} from "../../../../../constants/calendarDndTypes";

interface UseCalendarAppointmentDropProps {
  getRescheduledAppointment: (
    appointment: AppointmentWithSeriesInfo & { invoice?: InvoiceData },
  ) => AppointmentWithSeriesInfo & { invoice?: InvoiceData };
}

const useCalendarAppointmentDrop = ({
  getRescheduledAppointment,
}: UseCalendarAppointmentDropProps) => {
  const [droppedAppointment, setDroppedAppointment] = useState<
    (AppointmentWithSeriesInfo & { invoice?: InvoiceData }) | null
  >(null);

  const [{ isOver, dragOverItem }, drop] = useDrop<
    CalendarDndItem,
    CalendarDndDropResult,
    {
      isOver: boolean;
      dragOverItem: CalendarDndItem;
    }
  >(
    () => ({
      accept: CalendarDndItemTypes.APPOINTMENT,
      drop: (item) => {
        if (!item) {
          return { isValid: false };
        }
        const rescheduledAppointment = getRescheduledAppointment(
          item.appointment,
        );
        if (
          new Date(rescheduledAppointment.appointment_start).getTime() <
          Date.now()
        ) {
          return { isValid: false };
        }
        setDroppedAppointment(rescheduledAppointment);
        return {
          isValid: true,
          rescheduledAppointment,
          onSuccess: () => {
            setDroppedAppointment(null);
          },
          onError: () => {
            setDroppedAppointment(null);
          },
        };
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver(),
        dragOverItem: monitor.getItem(),
      }),
    }),
    [getRescheduledAppointment, setDroppedAppointment],
  );

  const temporaryAppointment = isOver
    ? getRescheduledAppointment(dragOverItem.appointment)
    : droppedAppointment;

  return { drop, temporaryAppointment, dragOverItem };
};

export default useCalendarAppointmentDrop;
